import { MaterialIcons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import { useFocusEffect } from '@react-navigation/native';
import { Stack, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, KeyboardAvoidingView, Linking, Modal, Platform, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';

import PhoneNumberInput from '@/components/PhoneNumberInput';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { PARTY_COLORS, getBorderColorForLightColor, getPartyColor } from '@/constants/Colors';
import AppStyles from '@/constants/Styles';
import { useCampaignContext } from '@/hooks/useCampaignContext';
import { useSafeKeepAwake } from '@/hooks/useSafeKeepAwake';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { Voter } from '@/types/canvassing';
import { normalizePhoneNumber } from '@/utils/phoneValidation';

const VI_OPTIONS = [
  { code: 'R', label: 'Reform', color: PARTY_COLORS['R'] },
  { code: 'Z', label: 'Possible Reform', color: PARTY_COLORS['Z'] },
  { code: 'A', label: 'Against Reform', color: PARTY_COLORS['A'] },
  { code: 'C', label: 'Conservative', color: PARTY_COLORS['C'] },
  { code: 'G', label: 'Green', color: PARTY_COLORS['G'] },
  { code: 'I', label: 'Independant', color: PARTY_COLORS['I'] },
  { code: 'L', label: 'Liberal Democrat', color: PARTY_COLORS['L'] },
  { code: 'N', label: 'Nationalist', color: PARTY_COLORS['N'] },
  { code: 'S', label: 'Socialist (Labour)', color: PARTY_COLORS['S'] },
  { code: 'U', label: 'Undecided', color: PARTY_COLORS['U'] },
  { code: 'W', label: 'Will not vote', color: PARTY_COLORS['W'] },
  { code: null, label: 'Clear', color: '#FFFFFF' },
];

// GOTV options for postal voters (excludes lift option)
const GOTV_OPTIONS_POSTAL = [
  { code: 'voted_reform', label: 'Voted Reform', icon: 'check-circle', letter: 'VR' },
  { code: 'voted_against', label: 'Voted Against', icon: 'check-circle', letter: 'VA' },
  { code: 'voted_yet', label: 'Voted Other', icon: 'check-circle', letter: 'V' },
  { code: 'will_vote', label: 'Todo (Will vote later)', icon: 'how-to-vote', letter: 'T' },
  { code: 'will_not_vote', label: 'Wont Vote', icon: 'cancel', letter: 'W' },
  { code: null, label: 'Clear', icon: 'clear', letter: '' },
];

// GOTV options for non-postal voters (includes lift option)
const GOTV_OPTIONS = [
  ...GOTV_OPTIONS_POSTAL.slice(0, -3), // All postal options except 'Clear'
  { code: 'requested_lift', label: 'Lift Requested', icon: 'directions-car', letter: 'L' },
  ...GOTV_OPTIONS_POSTAL.slice(-3), // Add 'Clear' at the end
];

interface HouseholdScreenProps {
  addressId: string;
  engagementId: string;
  battlePlanId: string;
  wakeLockId: string;
  isPhoneEngagement?: boolean;
}

export default function HouseholdScreen({ 
  addressId, 
  engagementId, 
  battlePlanId, 
  wakeLockId,
  isPhoneEngagement = false 
}: HouseholdScreenProps) {
  const router = useRouter();
  const { updateVoterData, getVoterData, updateAddressData } = useCampaignContext();
  const { getSelectedBranch, getSelectedCampaign, getElectionForCampaign } = useNavigationStore();

  // Theme-aware colors using Colors.ts values
  const textColor = useThemeColor({}, 'text');
  const iconColor = useThemeColor({}, 'icon');
  const inputBackground = useThemeColor({}, 'inputBackground' as any);
  const inputBorder = useThemeColor({}, 'inputBorder' as any);
  const modalBackground = useThemeColor({}, 'formBackground' as any);
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const cardBorder = useThemeColor({}, 'border' as any);
  const tintColor = useThemeColor({}, 'tint');

  // Fetch address data from store
  const getAddressesForEngagement = useCampaignStore(state => state.getAddressesForEngagement);
  const addresses = getAddressesForEngagement(engagementId);
  const address = addresses.find(addr => addr.address_id === addressId);
  
  // Get election data for GOTV date checking
  const selectedBranch = getSelectedBranch();
  const selectedCampaign = getSelectedCampaign();
  const selectedElection = selectedCampaign ? getElectionForCampaign(selectedCampaign, selectedBranch?.branch_short_name || '') : null;
  const getBattlePlans = useCampaignStore(state => state.getBattlePlans);
  const battlePlans = getBattlePlans();
  const selectedBattlePlan = battlePlans?.find(bp => bp.battle_plan_id === battlePlanId);
  
  const voters: Voter[] = address?.voters || [];
  const fullAddress = address ? [
    address.house_name,
    address.house_number,
    address.street_sub_name,
    address.street_name,
  ].filter(Boolean).join(', ') : '';

  // Get voters with offline updates merged
  const votersWithUpdates = voters?.map((v: Voter) => getVoterData(v.voter_id, v)) || [];
  
  // Helper function to check if today is polling day
  const isPollingDay = () => {
    if (!selectedElection) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const pollingDate = new Date(selectedElection.polling_day);
    pollingDate.setHours(0, 0, 0, 0);
    
    return today.getTime() === pollingDate.getTime();
  };

  // Helper function to check if GOTV date has arrived for a voter
  const isGotvDateArrived = (voter: Voter) => {
    if (!selectedElection) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for comparison
    
    // For postal voters, show GOTV fields from postal_votes_issued to polling_day (inclusive)
    if (voter.av_description === 'Postal') {
      // If postal_votes_issued is not available, fall back to polling day only
      if (!selectedElection.postal_votes_issued) {
        return isPollingDay();
      }
      
      const postalDate = new Date(selectedElection.postal_votes_issued);
      postalDate.setHours(0, 0, 0, 0);
      
      const pollingDate = new Date(selectedElection.polling_day);
      pollingDate.setHours(0, 0, 0, 0);
      
      // Show from postal vote day to polling day (inclusive)
      return today >= postalDate && today <= pollingDate;
    }
    
    // For ballot box voters, only show on polling day
    return isPollingDay();
  };

  // Helper function to get GOTV options for a voter
  const getGotvOptions = (voter: Voter) => {
    const isPostal = voter.av_description?.toLowerCase() === 'postal';
    
    // On polling day, postal voters can also request lifts
    if (isPostal && isPollingDay()) {
      return GOTV_OPTIONS;
    }
    
    // Otherwise, postal voters use reduced options
    return isPostal ? GOTV_OPTIONS_POSTAL : GOTV_OPTIONS;
  };
  
  const [voterEdits, setVoterEdits] = useState<Voter[]>(
    votersWithUpdates.map((v: Voter) => ({
      ...v,
      latest_vi: v.latest_vi || '',
      key_issues: v.key_issues || '',
      phone_number: v.phone_number || '',
      email: v.email || '',
      reform_window_poster: v.reform_window_poster || '',
      reform_garden_board: v.reform_garden_board || '',
      // Initialize GOTV fields
      voted_yet: v.voted_yet || false,
      has_been_contacted: v.has_been_contacted || false,
      will_not_vote: v.will_not_vote || false,
      will_vote_by_time: v.will_vote_by_time || '',
      requested_lift: v.requested_lift || false,
      gotv_notes: v.gotv_notes || '',
    }))
  );
  
  // Track original voter data for comparison
  const [originalVoters, setOriginalVoters] = useState<Voter[]>(
    votersWithUpdates.map((v: Voter) => ({
      ...v,
      latest_vi: v.latest_vi || '',
      key_issues: v.key_issues || '',
      phone_number: v.phone_number || '',
      email: v.email || '',
      reform_window_poster: v.reform_window_poster || '',
      reform_garden_board: v.reform_garden_board || '',
      // Initialize GOTV fields
      voted_yet: v.voted_yet || false,
      has_been_contacted: v.has_been_contacted || false,
      will_not_vote: v.will_not_vote || false,
      will_vote_by_time: v.will_vote_by_time || '',
      requested_lift: v.requested_lift || false,
      gotv_notes: v.gotv_notes || '',
    }))
  );
  
  const [focusedVoterId, setFocusedVoterId] = useState<string | null>(null);
  const [inputHeights, setInputHeights] = useState<{ [voterId: string]: number }>({});
  const [viDropdownVisible, setViDropdownVisible] = useState<string | null>(null);
  const [gotvDropdownVisible, setGotvDropdownVisible] = useState<string | null>(null);
  const [showNotInDialog, setShowNotInDialog] = useState<boolean>(false);
  const [editingPhone, setEditingPhone] = useState<Set<string>>(new Set());
  const [editingEmail, setEditingEmail] = useState<Set<string>>(new Set());
  const [expandedMore, setExpandedMore] = useState<Set<string>>(new Set());
  
  // Phone number modal state for lift requests
  const [showPhoneModal, setShowPhoneModal] = useState<boolean>(false);
  const [phoneModalVoterId, setPhoneModalVoterId] = useState<string | null>(null);
  const [phoneModalNumber, setPhoneModalNumber] = useState<string>('');
  const [phoneModalValid, setPhoneModalValid] = useState<boolean>(false);
  
  // Reform dialog state (shows after selecting "R" as VI)
  const [showReformDialog, setShowReformDialog] = useState<boolean>(false);
  const [reformDialogVoterId, setReformDialogVoterId] = useState<string | null>(null);
  const [reformDialogEditingPhone, setReformDialogEditingPhone] = useState<boolean>(false);
  const [reformDialogEditingEmail, setReformDialogEditingEmail] = useState<boolean>(false);
  const [reformDialogPhoneNumber, setReformDialogPhoneNumber] = useState<string>('');
  const [reformDialogEmail, setReformDialogEmail] = useState<string>('');
  const [reformDialogGardenBoard, setReformDialogGardenBoard] = useState<string | null>(null);
  const [reformDialogWindowPoster, setReformDialogWindowPoster] = useState<string | null>(null);
  
  const scrollViewRef = useRef<ScrollView>(null);
  const compareAndUpdateVotersRef = useRef<(() => Promise<void>) | null>(null);
  
  // Local state for text inputs to avoid re-rendering entire voter list on every keystroke
  const [localTextValues, setLocalTextValues] = useState<{
    [key: string]: {
      key_issues?: string;
      gotv_notes?: string;
      phone_number?: string;
      email?: string;
    }
  }>({});

  // Update voter edits when address data changes
  useEffect(() => {
    if (address && address.voters) {
      const updatedVotersWithUpdates = voters.map((v: Voter) => getVoterData(v.voter_id, v));
      const normalizedVoters = updatedVotersWithUpdates.map((v: Voter) => ({
        ...v,
        latest_vi: v.latest_vi || '',
        key_issues: v.key_issues || '',
        
        reform_window_poster: v.reform_window_poster || '',
        reform_garden_board: v.reform_garden_board || '',
        
        phone_number: v.phone_number || '',
        email: v.email || '',
        voted_yet: v.voted_yet || false,
        has_been_contacted: v.has_been_contacted || false,
        will_not_vote: v.will_not_vote || false,
        will_vote_by_time: v.will_vote_by_time || '',
        requested_lift: v.requested_lift || false,
        gotv_notes: v.gotv_notes || '',
      }));
      
      // Update both original and current edits when address changes
      setOriginalVoters(normalizedVoters);
      setVoterEdits(normalizedVoters);
    }
  }, [address?.address_id, voters.length]); // Only depend on address ID and voters count

  // Reset dialog state when addressId changes (new household loaded)
  useEffect(() => {
    setShowNotInDialog(false);
    setShowReformDialog(false);
    setReformDialogVoterId(null);
  }, [addressId]);

  // Wake lock functionality - keep screen on during household canvassing
  // Automatically disabled on iOS platforms by useSafeKeepAwake hook
  useSafeKeepAwake(wakeLockId, { 
    suppressDeactivateWarnings: true 
  });

  // Early return if address not found
  if (!address) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <Stack.Screen options={{ title: 'Address Not Found' }} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ThemedText>Address not found</ThemedText>
        </View>
      </ThemedView>
    );
  }

  // Define GOTV and non-GOTV fields for separation
  const GOTV_FIELDS = ['voted_yet', 'will_not_vote', 'has_been_contacted', 'will_vote_by_time', 'requested_lift', 'gotv_notes'];
  const NON_GOTV_FIELDS = ['latest_vi', 'latest_vi_datetime', 'key_issues', 'phone_number', 'email', 'reform_window_poster', 'reform_garden_board'];

  // Compare original and edited voters and batch update changes
  const compareAndUpdateVoters = useCallback(async () => {
    // First, sync any pending localTextValues to voterEdits
    // This handles cases where blur hasn't fired (e.g., user closes while typing)
    let updatedVoterEdits = [...voterEdits];
    
    for (const voterId in localTextValues) {
      const localValues = localTextValues[voterId];
      if (!localValues) continue;
      
      updatedVoterEdits = updatedVoterEdits.map(v => {
        if (v.voter_id !== voterId) return v;
        
        return {
          ...v,
          ...(localValues.key_issues !== undefined && { key_issues: localValues.key_issues }),
          ...(localValues.phone_number !== undefined && { phone_number: localValues.phone_number }),
          ...(localValues.email !== undefined && { email: localValues.email }),
          ...(localValues.gotv_notes !== undefined && { gotv_notes: localValues.gotv_notes }),
        };
      });
    }
    
    // Update voterEdits state with synced values
    setVoterEdits(updatedVoterEdits);
    
    // Now compare and update
    for (const editedVoter of updatedVoterEdits) {
      const originalVoter = originalVoters.find(v => v.voter_id === editedVoter.voter_id);
      if (!originalVoter) continue;

      const gotvUpdates: any = {};
      const nonGotvUpdates: any = {};
      let hasGotvChanges = false;
      let hasNonGotvChanges = false;

      // Check GOTV fields
      for (const field of GOTV_FIELDS) {
        const originalValue = (originalVoter as any)[field];
        const editedValue = (editedVoter as any)[field];
        
        if (originalValue !== editedValue) {
          gotvUpdates[field] = editedValue;
          hasGotvChanges = true;
        }
      }

      // Check non-GOTV fields
      for (const field of NON_GOTV_FIELDS) {
        const originalValue = (originalVoter as any)[field];
        const editedValue = (editedVoter as any)[field];
        
        if (originalValue !== editedValue) {
          nonGotvUpdates[field] = editedValue;
          hasNonGotvChanges = true;
        }
      }

      // Make separate API calls for GOTV and non-GOTV updates
      try {
        if (hasNonGotvChanges) {
          await updateVoterData(editedVoter.voter_id, {
            ...nonGotvUpdates,
            address_id: addressId,
            engagement_id: engagementId,
          });
        }

        if (hasGotvChanges) {
          await updateVoterData(editedVoter.voter_id, {
            ...gotvUpdates,
            address_id: addressId,
            engagement_id: engagementId,
          });
        }

        // Update original voters to reflect the new baseline after successful update
        if (hasNonGotvChanges || hasGotvChanges) {
          setOriginalVoters(prev => 
            prev.map(v => v.voter_id === editedVoter.voter_id ? { ...editedVoter } : v)
          );
        }
      } catch (error) {
        console.error(`Error updating voter ${editedVoter.voter_id}:`, error);
      }
    }
  }, [voterEdits, localTextValues, originalVoters, addressId, engagementId, updateVoterData]);

  // Keep ref updated with latest function
  compareAndUpdateVotersRef.current = compareAndUpdateVoters;

  // Save changes when navigating away from screen (back button, gestures, etc.)
  useFocusEffect(
    useCallback(() => {
      // Return cleanup function that runs when screen loses focus
      return () => {
        // Save any pending changes when navigating away
        if (compareAndUpdateVotersRef.current) {
          compareAndUpdateVotersRef.current();
        }
      };
    }, []) // Empty dependency array to prevent infinite loops
  );

  const handleNotIn = async () => {
    setShowNotInDialog(true);
  };

  const confirmNotIn = async () => {
    setShowNotInDialog(false);
    try {
      // Update address data to mark as not in - sync service will handle interaction creation
      await updateAddressData(addressId, {
        not_in: true,
        engagement_id: engagementId,
      });
      
      router.back();
    } catch {
      Alert.alert('Error', 'Failed to mark household as not in.');
    }
  };

  const cancelNotIn = () => {
    setShowNotInDialog(false);
  };

  const handleViChange = async (voterId: string, newVi: string | null) => {
    const now = new Date().toISOString();
    
    // Get the current voter before updating
    const currentVoter = voterEdits.find(v => v.voter_id === voterId);
    
    // Update local state immediately for responsive UI
    setVoterEdits((edits: Voter[]) => edits.map((v: Voter) => 
      v.voter_id === voterId ? { ...v, latest_vi: newVi, latest_vi_datetime: now } : v
    ));

    // If "R" (Reform) is selected, open the reform dialog immediately.
    // This must not wait on any network/sync work to avoid a noticeable gap
    // between closing the VI dropdown and showing the dialog.
    if (newVi === 'R' && currentVoter) {
      setReformDialogVoterId(voterId);
      setReformDialogPhoneNumber(currentVoter.phone_number || '');
      setReformDialogEmail(currentVoter.email || '');
      setReformDialogGardenBoard(currentVoter.reform_garden_board || null);
      setReformDialogWindowPoster(currentVoter.reform_window_poster || null);
      setReformDialogEditingPhone(!currentVoter.have_phone_number);
      setReformDialogEditingEmail(!currentVoter.have_email_address);
      setShowReformDialog(true);
    }
    
    // Update offline/network state in the background so UI is instant.
    // (If it fails, we log the error; the local UI state remains responsive.)
    void updateVoterData(voterId, {
      latest_vi: newVi,
      latest_vi_datetime: now,
      address_id: addressId,
      engagement_id: engagementId,
    })
      .then(() => {
        // Update original voters to reflect the change (prevents duplicate updates)
        setOriginalVoters(prev =>
          prev.map(v => (v.voter_id === voterId ? { ...v, latest_vi: newVi, latest_vi_datetime: now } : v))
        );
      })
      .catch((error) => {
        console.error('Error in updateVoterData for VI:', error);
      });
  };

  const handleIssuesChange = useCallback((voterId: string, newIssues: string) => {
    // Update local state only - no re-render of entire voter list
    setLocalTextValues(prev => ({
      ...prev,
      [voterId]: {
        ...prev[voterId],
        key_issues: newIssues
      }
    }));
  }, []);

  const handleIssuesBlur = useCallback((voterId: string) => {
    const localValue = localTextValues[voterId]?.key_issues;
    const currentValue = voterEdits.find(v => v.voter_id === voterId)?.key_issues || '';
    
    // Update voterEdits for immediate UI consistency
    if (localValue !== currentValue) {
      setVoterEdits((edits: Voter[]) => edits.map((v: Voter) => 
        v.voter_id === voterId ? { ...v, key_issues: localValue || '' } : v
      ));

      compareAndUpdateVoters();
    }
  }, [localTextValues, voterEdits]);

  const handlePhoneChange = useCallback((voterId: string, newPhone: string) => {
    // Update local state only - comparison happens on blur
    setLocalTextValues(prev => ({
      ...prev,
      [voterId]: {
        ...prev[voterId],
        phone_number: newPhone
      }
    }));
  }, []);

  const handlePhoneBlur = useCallback((voterId: string) => {
    const localValue = localTextValues[voterId]?.phone_number;
    const currentValue = voterEdits.find(v => v.voter_id === voterId)?.phone_number || '';
    
    // Update voterEdits for immediate UI consistency
    if (localValue !== currentValue) {
      setVoterEdits((edits: Voter[]) => edits.map((v: Voter) => 
        v.voter_id === voterId ? { ...v, phone_number: localValue || '' } : v
      ));

      compareAndUpdateVoters();
    }
  }, [localTextValues, voterEdits]);

  const handleEmailChange = useCallback((voterId: string, newEmail: string) => {
    // Update local state only - no re-render of entire voter list
    setLocalTextValues(prev => ({
      ...prev,
      [voterId]: {
        ...prev[voterId],
        email: newEmail
      }
    }));
  }, []);

  const handleEmailBlur = useCallback((voterId: string) => {
    const localValue = localTextValues[voterId]?.email;
    const currentValue = voterEdits.find(v => v.voter_id === voterId)?.email || '';
    
    // Update voterEdits for immediate UI consistency
    if (localValue !== currentValue) {
      setVoterEdits((edits: Voter[]) => edits.map((v: Voter) => 
        v.voter_id === voterId ? { ...v, email: localValue || '' } : v
      ));

      compareAndUpdateVoters();
    }
  }, [localTextValues, voterEdits]);

  const handleGotvNotesChange = useCallback((voterId: string, newNotes: string) => {
    // Update local state only - no re-render of entire voter list
    setLocalTextValues(prev => ({
      ...prev,
      [voterId]: {
        ...prev[voterId],
        gotv_notes: newNotes
      }
    }));
  }, []);

  const handleGotvNotesBlur = useCallback((voterId: string) => {
    const localValue = localTextValues[voterId]?.gotv_notes;
    const currentValue = voterEdits.find(v => v.voter_id === voterId)?.gotv_notes || '';
    
    // Update voterEdits for immediate UI consistency
    if (localValue !== currentValue) {
      setVoterEdits((edits: Voter[]) => edits.map((v: Voter) => 
        v.voter_id === voterId ? { ...v, gotv_notes: localValue || '' } : v
      ));

      compareAndUpdateVoters();
    }
  }, [localTextValues, voterEdits]);

  const handleGotvChange = async (voterId: string, field: string, value: any) => {
    const updates: any = {
      [field]: value,
      address_id: addressId,
      engagement_id: engagementId,
    };

    // Update local state immediately for responsive UI
    setVoterEdits((edits: Voter[]) => 
      edits.map((v: Voter) => 
        v.voter_id === voterId ? { ...v, ...updates } : v
      )
    );
    
    try {
      // Update offline state - sync service will handle server updates and interaction creation
      await updateVoterData(voterId, updates);
      
      // Update original voters to reflect the change (prevents duplicate updates)
      setOriginalVoters(prev => 
        prev.map(v => v.voter_id === voterId ? { ...v, [field]: value } : v)
      );
    } catch (error) {
      console.error('Error in updateVoterData for GOTV:', error);
    }
  };

  // Helper function to get current GOTV status for display
  const getGotvStatus = (voter: Voter): { code: string; label: string; icon: string; letter: string } | null => {
    if (voter.voted_yet) {
      // Determine which "Voted" option based on VI value
      if (voter.latest_vi === 'R') {
        return { code: 'voted_reform', label: 'Voted Reform', icon: 'check-circle', letter: 'VR' };
      }
      // S, C, I, L, G, N all count as against
      if (['A', 'S', 'C', 'I', 'L', 'G', 'N'].includes(voter.latest_vi || '')) {
        return { code: 'voted_against', label: 'Voted Against', icon: 'check-circle', letter: 'VA' };
      }
      return { code: 'voted_yet', label: 'Voted', icon: 'check-circle', letter: 'V' };
    }
    if (voter.will_not_vote) return { code: 'will_not_vote', label: 'Will Not Vote', icon: 'cancel', letter: 'W' };
    if (voter.requested_lift) return { code: 'requested_lift', label: 'Requested Lift', icon: 'directions-car', letter: 'L' };
    // Backward compatibility: has_been_contacted means will vote (todo)
    if (voter.has_been_contacted) {
      return { code: 'will_vote', label: 'Will Vote', icon: 'how-to-vote', letter: 'T' };
    }
    
    return null;
  };

  // Handle GOTV status change
  const handleGotvStatusChange = async (voterId: string, statusCode: string | null) => {
    // Special handling for lift requests - check if phone number is required
    if (statusCode === 'requested_lift') {
      const voter = voterEdits.find(v => v.voter_id === voterId);
      if (voter && (!voter.have_phone_number && (!voter.phone_number || voter.phone_number.trim() === ''))) {
        // No phone number - show modal to collect it
        setPhoneModalVoterId(voterId);
        setPhoneModalNumber('');
        setPhoneModalValid(false);
        setShowPhoneModal(true);
        return; // Don't proceed with lift request yet
      }
    }

    const voter = voterEdits.find(v => v.voter_id === voterId);
    const now = new Date().toISOString();
    
    const gotvUpdates: any = {
      voted_yet: false,
      will_not_vote: false,
      has_been_contacted: false,
      requested_lift: false,
      address_id: addressId,
      engagement_id: engagementId,
    };

    let viUpdate: { latest_vi: string; latest_vi_datetime: string } | null = null;

    if (statusCode) {
      // Handle new voting options
      if (statusCode === 'voted_reform') {
        gotvUpdates.voted_yet = true;
        // Update VI to 'R' if it's not already 'R'
        if (voter && voter.latest_vi !== 'R') {
          viUpdate = { latest_vi: 'R', latest_vi_datetime: now };
        }
      } else if (statusCode === 'voted_against') {
        gotvUpdates.voted_yet = true;
        // Update VI to 'A' if current VI is R, Z, W, U
        if (voter && ['R', 'Z', 'W', 'U'].includes(voter.latest_vi || '')) {
          viUpdate = { latest_vi: 'A', latest_vi_datetime: now };
        }
      } else if (statusCode === 'voted_yet') {
        gotvUpdates.voted_yet = true;
        // Don't change VI for general "Voted" option
      } else {
        gotvUpdates[statusCode] = true;
      }
      
      // Set has_been_contacted to true when voter commits to voting or not voting
      if (statusCode === 'will_vote' || statusCode === 'will_not_vote' || statusCode === 'voted_reform' || statusCode === 'voted_against' || statusCode === 'voted_yet') {
        gotvUpdates.has_been_contacted = true;
      }
      
      // Clear will_vote_by_time when will_not_vote is selected
      if (statusCode === 'will_not_vote') {
        gotvUpdates.will_vote_by_time = null;
      }
    }

    // Combine all updates for local state
    const allUpdates = { ...gotvUpdates, ...(viUpdate || {}) };

    // Update local state immediately for responsive UI
    setVoterEdits((edits: Voter[]) => 
      edits.map((v: Voter) => 
        v.voter_id === voterId ? { ...v, ...allUpdates } : v
      )
    );
    
    try {
      // Update GOTV status first
      await updateVoterData(voterId, gotvUpdates);
      
      // Update VI separately if needed (this ensures it goes through the correct API)
      if (viUpdate) {
        await updateVoterData(voterId, {
          ...viUpdate,
          address_id: addressId,
          engagement_id: engagementId,
        });
      }
      
      // Update original voters to reflect the change (prevents duplicate updates)
      setOriginalVoters(prev => 
        prev.map(v => v.voter_id === voterId ? { ...v, ...allUpdates } : v)
      );
    } catch (error) {
      console.error('Error in updateVoterData for GOTV status:', error);
    }
  };

  // Handle phone modal actions
  const handlePhoneModalCancel = () => {
    setShowPhoneModal(false);
    setPhoneModalVoterId(null);
    setPhoneModalNumber('');
    setPhoneModalValid(false);
  };

  // Reform dialog handlers
  const handleReformDialogCancel = () => {
    setShowReformDialog(false);
    setReformDialogVoterId(null);
    setReformDialogEditingPhone(false);
    setReformDialogEditingEmail(false);
  };

  const handleReformDialogConfirm = async () => {
    if (!reformDialogVoterId) return;

    const updates: any = {
      address_id: addressId,
      engagement_id: engagementId,
    };

    // Add phone number if provided
    if (reformDialogPhoneNumber.trim()) {
      updates.phone_number = normalizePhoneNumber(reformDialogPhoneNumber);
    }

    // Add email if provided
    if (reformDialogEmail.trim()) {
      updates.email = reformDialogEmail.trim();
    }

    // Add garden board and window poster
    updates.reform_garden_board = reformDialogGardenBoard;
    updates.reform_window_poster = reformDialogWindowPoster;

    try {
      // Update offline state - sync service will handle server updates
      await updateVoterData(reformDialogVoterId, updates);

      // Update local state
      setVoterEdits((edits: Voter[]) => 
        edits.map((v: Voter) => 
          v.voter_id === reformDialogVoterId 
            ? { 
                ...v, 
                phone_number: reformDialogPhoneNumber.trim() ? normalizePhoneNumber(reformDialogPhoneNumber) : (v.phone_number || ''),
                email: reformDialogEmail.trim() || (v.email || ''),
                reform_garden_board: reformDialogGardenBoard || '',
                reform_window_poster: reformDialogWindowPoster || '',
              } 
            : v
        )
      );

      // Update original voters
      setOriginalVoters(prev => 
        prev.map(v => 
          v.voter_id === reformDialogVoterId 
            ? { 
                ...v, 
                phone_number: reformDialogPhoneNumber.trim() ? normalizePhoneNumber(reformDialogPhoneNumber) : (v.phone_number || ''),
                email: reformDialogEmail.trim() || (v.email || ''),
                reform_garden_board: reformDialogGardenBoard || '',
                reform_window_poster: reformDialogWindowPoster || '',
              } 
            : v
        )
      );
    } catch (error) {
      console.error('Error updating voter data in reform dialog:', error);
      Alert.alert('Error', 'Failed to save updates. Please try again.');
    }

    // Close dialog
    handleReformDialogCancel();
  };

  const handlePhoneModalConfirm = async () => {
    if (!phoneModalVoterId || !phoneModalValid || !phoneModalNumber.trim()) {
      return;
    }

    // Normalize the phone number
    const normalizedPhone = normalizePhoneNumber(phoneModalNumber);
    
    try {
      // First update: Phone number (main details)
      const phoneUpdates = {
        phone_number: normalizedPhone,
        address_id: addressId,
        engagement_id: engagementId,
      };

      // Update local state immediately for responsive UI
      setVoterEdits((edits: Voter[]) => 
        edits.map((v: Voter) => 
          v.voter_id === phoneModalVoterId ? { ...v, ...phoneUpdates } : v
        )
      );
      
      // Update offline state for phone number
      await updateVoterData(phoneModalVoterId, phoneUpdates);
      
      // Update original voters to reflect the phone number change
      setOriginalVoters(prev => 
        prev.map(v => v.voter_id === phoneModalVoterId ? { ...v, ...phoneUpdates } : v)
      );

      // Second update: GOTV status (lift request)
      const gotvUpdates = {
        requested_lift: true,
        has_been_contacted: true,
        address_id: addressId,
        engagement_id: engagementId,
      };

      // Update local state for GOTV status
      setVoterEdits((edits: Voter[]) => 
        edits.map((v: Voter) => 
          v.voter_id === phoneModalVoterId ? { ...v, ...gotvUpdates } : v
        )
      );
      
      // Update offline state for GOTV status
      await updateVoterData(phoneModalVoterId, gotvUpdates);
      
      // Update original voters to reflect the GOTV change
      setOriginalVoters(prev => 
        prev.map(v => v.voter_id === phoneModalVoterId ? { ...v, ...gotvUpdates } : v)
      );

    } catch (error) {
      console.error('Error in updateVoterData for phone number and lift request:', error);
    }

    // Close the modal
    handlePhoneModalCancel();
  };

  const handleClose = async () => {
    // Save any pending changes before closing
    router.back();
  };

  const handlePhoneCall = async (phoneNumber: string) => {
    try {
      const phoneUrl = `tel:${phoneNumber}`;
      const canOpen = await Linking.canOpenURL(phoneUrl);
      
      if (canOpen) {
        await Linking.openURL(phoneUrl);
      } else {
        Alert.alert('Error', 'Unable to make phone calls on this device.');
      }
    } catch (error) {
      console.error('Error opening phone dialer:', error);
      Alert.alert('Error', 'Unable to make phone calls on this device.');
    }
  };

  const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
    // Clean the phone number for display
    let cleaned = phoneNumber.replace(/\s+/g, ' ').trim();
    // If it doesn't start with + or 0, add a 0 at the start
    if (cleaned && !cleaned.startsWith('+') && !cleaned.startsWith('0')) {
      cleaned = '0' + cleaned;
    }
    return cleaned;
  };

  return (
    <ThemedView style={{ flex: 1 }}>
      <Stack.Screen options={{ title: fullAddress || 'Household' }} />
      <KeyboardAvoidingView 
        style={{ flex: 1 }} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          ref={scrollViewRef}
          keyboardShouldPersistTaps="handled" 
          style={AppStyles.householdScrollView}
          contentContainerStyle={{ paddingBottom: 100 }} // Add extra padding at bottom
          showsVerticalScrollIndicator={true}
        >
        {/* Not In Button */}
        <View style={AppStyles.householdButtonContainer}>
          <TouchableOpacity
            style={[
              AppStyles.primaryButton,
              {
                backgroundColor:'#dc3545',
                opacity: 1,
              }
            ]}
            onPress={handleNotIn}
          >
            <ThemedText style={AppStyles.primaryButtonText}>
             Not In
            </ThemedText>
          </TouchableOpacity>
        </View>

        {/* Voter List */}
        {voterEdits.map((voter: Voter) => (
          <View key={voter.voter_id} style={[
            AppStyles.voterVISelectionRow,
            {
              backgroundColor: cardBackground,
              borderColor: cardBorder,
            }
          ]}>
            <View style={AppStyles.nameViRow}>
              <View style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <ThemedText type="title" style={AppStyles.headerText}>{voter.name}</ThemedText>
                  {voter.av_description === 'Postal' && (
                    <MaterialIcons name="email" size={18} color={iconColor} style={{ marginLeft: 8 }} />
                  )}
                </View>
                {/* Phone Number Display */}
                {voter.phone_number && voter.phone_number.trim() && (
                  <TouchableOpacity
                    onPress={() => handlePhoneCall(voter.phone_number!)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginTop: 4,
                      paddingVertical: 6,
                      paddingHorizontal: 10,
                      backgroundColor: isPhoneEngagement ? tintColor + '30' : tintColor + '20', // More prominent for phone engagement
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: isPhoneEngagement ? tintColor : tintColor + '40',
                      shadowColor: isPhoneEngagement ? tintColor : 'transparent',
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: isPhoneEngagement ? 0.3 : 0,
                      shadowRadius: 2,
                      elevation: isPhoneEngagement ? 2 : 0,
                    }}
                    activeOpacity={0.7}
                  >
                    <MaterialIcons 
                      name={isPhoneEngagement ? "phone" : "phone"} 
                      size={18} 
                      color={tintColor} 
                      style={{ marginRight: 8 }} 
                    />
                    <ThemedText style={{ 
                      color: tintColor, 
                      fontSize: isPhoneEngagement ? 17 : 16, 
                      fontWeight: isPhoneEngagement ? '700' : '600',
                      textDecorationLine: 'underline'
                    }}>
                      {formatPhoneNumberForDisplay(voter.phone_number)}
                    </ThemedText>
                    {isPhoneEngagement && (
                      <MaterialIcons 
                        name="call" 
                        size={14} 
                        color={tintColor} 
                        style={{ marginLeft: 4 }} 
                      />
                    )}
                  </TouchableOpacity>
                )}
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {/* Show VI letter outside and to the left of GOTV dropdown */}
                {isGotvDateArrived(voter) && selectedBattlePlan?.election_id && voter.latest_vi && (
                  <ThemedText style={AppStyles.voterVi}>
                    {voter.latest_vi}
                  </ThemedText>
                )}
                <View style={AppStyles.viPickerContainer}>
                  {/* Show GOTV dropdown if GOTV date has arrived, otherwise show VI dropdown */}
                  {isGotvDateArrived(voter) && selectedBattlePlan?.election_id ? (
                    <View style={[
                      AppStyles.viPickerOutline,
                      {
                        backgroundColor: inputBackground,
                        borderColor: inputBorder,
                      }
                    ]}>
                      <TouchableOpacity
                        onPress={() => setGotvDropdownVisible(voter.voter_id)}
                        style={AppStyles.viTextInput}
                        activeOpacity={0.7}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          {/* Show icon if GOTV status is selected */}
                          {(() => {
                            const gotvStatus = getGotvStatus(voter);
                            return gotvStatus && gotvStatus.icon ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <MaterialIcons 
                                  name={gotvStatus.icon as any} 
                                  size={18} 
                                  color={textColor} 
                                />
                                <ThemedText style={[AppStyles.viTextInputText, { color: textColor, fontSize: 12, fontWeight: 'bold' }]}>
                                  {gotvStatus.letter}
                                </ThemedText>
                              </View>
                            ) : (
                              <ThemedText style={[AppStyles.viTextInputText, { color: textColor }]}>
                                Select
                              </ThemedText>
                            );
                          })()}
                        </View>
                      </TouchableOpacity>
                    </View>
                  ) : (
                  <View style={[
                    AppStyles.viPickerOutline,
                    {
                      backgroundColor: inputBackground,
                      borderColor: inputBorder,
                    }
                  ]}>
                    <TouchableOpacity
                      onPress={() => setViDropdownVisible(voter.voter_id)}
                      style={AppStyles.viTextInput}
                      activeOpacity={0.7}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {/* Show color indicator if VI is selected */}
                        {Boolean(voter.latest_vi && getPartyColor(voter.latest_vi)) && (
                          <View 
                            style={[
                              AppStyles.viColorIndicator,
                              { backgroundColor: getPartyColor(voter.latest_vi!) },
                              // Add border for light colors to ensure visibility
                              getBorderColorForLightColor(getPartyColor(voter.latest_vi!)!) && {
                                borderWidth: 1,
                                borderColor: getBorderColorForLightColor(getPartyColor(voter.latest_vi!)!)
                              }
                            ]}
                          />
                        )}
                        <ThemedText style={[AppStyles.viTextInputText, { color: textColor }]}>
                          {voter.latest_vi || 'Select VI'}
                        </ThemedText>
                      </View>
                    </TouchableOpacity>
                  </View>
                )}
                </View>
              </View>
            </View>

            {/* GOTV Fields - Only show if GOTV date has arrived */}
            {isGotvDateArrived(voter) && selectedBattlePlan?.election_id && (
              <View>

                {/* Voting Date/Time - Show when voter has Todo status (will_vote) */}
                {(() => {
                  const gotvStatus = getGotvStatus(voter);
                  return gotvStatus && gotvStatus.code === 'will_vote' && selectedElection;
                })() && (() => {
                  const isPostal = voter.av_description === 'Postal';

                  const today = new Date();
                  const pollingDate = new Date(selectedElection!.polling_day);
                  
                  if (isPostal && today <= pollingDate) {
                    // Generate date options from tomorrow to polling day for postal voters
                    
                    today.setHours(0, 0, 0, 0);
                    pollingDate.setHours(0, 0, 0, 0);
                    
                    const dateOptions: { label: string; value: string }[] = [];
                    const tomorrow = new Date(today);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    
                    const currentDate = new Date(tomorrow);
                    
                    while (currentDate <= pollingDate) {
                      // Use toLocaleDateString with specific formatting for consistent YYYY-MM-DD format
                      const year = currentDate.getFullYear();
                      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                      const day = String(currentDate.getDate()).padStart(2, '0');
                      const dateString = `${year}-${month}-${day}T00:00:00`;
                      
                      const displayString = currentDate.toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      }) as string;
                      dateOptions.push({ 
                        label: (displayString || dateString) as string, 
                        value: dateString as string 
                      });
                      currentDate.setDate(currentDate.getDate() + 1);
                    }
                    
                    return (
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <ThemedText style={{ color: textColor, flex: 1 }}>
                          Will vote by
                        </ThemedText>
                        
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <View style={[AppStyles.pickerWrapper, {minWidth: 150}]}>
                            <Picker
                              selectedValue={voter.will_vote_by_time || ''}
                              onValueChange={(value) => {
                                if (value && value != "") {
                                  handleGotvChange(voter.voter_id, 'will_vote_by_time', value);
                                } else {
                                  handleGotvChange(voter.voter_id, 'will_vote_by_time', null);
                                }
                              }}
                              style={[AppStyles.pickerInput, { color: textColor }]}
                            >
                              <Picker.Item label="Select date" value="" color={textColor} />
                              {dateOptions.map((option) => (
                                <Picker.Item 
                                  key={option.value} 
                                  label={option.label} 
                                  value={option.value} 
                                  color={textColor}
                                />
                              ))}
                            </Picker>
                          </View>
                        </View>
                      </View>
                    );
                  } else {
                    // Generate time options from now until 21:00 for non-postal voters
                    const now = new Date();
                    const currentHour = now.getHours();
                    const currentMinute = now.getMinutes();
                    
                    const timeOptions: { label: string; value: string }[] = [];
                    
                    // Start from the next hour if current time is past the hour mark
                    let startHour = currentHour;
                    if (currentMinute > 0) {
                      startHour += 1;
                    }
                    
                    // Generate times from start hour to 21:00
                    for (let hour = startHour; hour <= 21; hour++) {
                      const today = new Date();
                      const yyyy = today.getFullYear();
                      const mm = (today.getMonth() + 1).toString().padStart(2, '0');
                      const dd = today.getDate().toString().padStart(2, '0');
                      const timeString = `${yyyy}-${mm}-${dd}T${hour.toString().padStart(2, '0')}:00:00`;
                      const displayTime = `${hour}:00`;
                      timeOptions.push({
                        label: displayTime,
                        value: timeString
                      });
                    }
                    
                    return (
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
                        <ThemedText style={{ color: textColor, flex: 1 }}>
                          Will vote by
                        </ThemedText>
                        
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <View style={[AppStyles.pickerWrapper, {minWidth: 150}]}>
                            <Picker
                              selectedValue={voter.will_vote_by_time || ''}
                              onValueChange={(value) => {
                                if (value && value != "") {
                                  handleGotvChange(voter.voter_id, 'will_vote_by_time', value);
                                } else {
                                  handleGotvChange(voter.voter_id, 'will_vote_by_time', null);
                                }
                              }}
                              style={[AppStyles.pickerInput, { color: textColor }]}
                            >
                              <Picker.Item label="Select time" value="" color={textColor} />
                              {timeOptions.map((option) => (
                                <Picker.Item 
                                  key={option.value} 
                                  label={option.label} 
                                  value={option.value} 
                                  color={textColor}
                                />
                              ))}
                            </Picker>
                          </View>
                        </View>
                      </View>
                    );
                  }
                })()}

                {/* GOTV Notes */}
                <TextInput
                  style={[
                    AppStyles.issuesInput,
                    {
                      backgroundColor: inputBackground,
                      borderColor: focusedVoterId === voter.voter_id ? '#007AFF' : inputBorder,
                      color: textColor,
                      height: inputHeights[`${voter.voter_id}_gotv`] || AppStyles.issuesInput.height,
                    },
                    focusedVoterId === voter.voter_id && {
                      shadowColor: '#007AFF',
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 2,
                    }
                  ]}
                  value={localTextValues[voter.voter_id]?.gotv_notes ?? voter.gotv_notes ?? ''}
                  onChangeText={text => handleGotvNotesChange(voter.voter_id, text)}
                  placeholder="GOTV notes"
                  placeholderTextColor={iconColor}
                  multiline
                  numberOfLines={2}
                  maxLength={300}
                  onFocus={() => setFocusedVoterId(voter.voter_id)}
                  onBlur={() => {
                    setFocusedVoterId(null);
                    handleGotvNotesBlur(voter.voter_id);
                  }}
                  onContentSizeChange={e => {
                    const minHeight = 48;
                    const maxHeight = 240; // 5 lines
                    let newHeight = e.nativeEvent.contentSize.height;
                    if (newHeight < minHeight) newHeight = minHeight;
                    if (newHeight > maxHeight) newHeight = maxHeight;
                    setInputHeights(h => ({ ...h, [`${voter.voter_id}_gotv`]: newHeight }));
                  }}
                />
              </View>
            )}

            {/* More Link */}
            <TouchableOpacity
              onPress={() => {
                setExpandedMore(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(voter.voter_id)) {
                    newSet.delete(voter.voter_id);
                  } else {
                    newSet.add(voter.voter_id);
                  }
                  return newSet;
                });
              }}
              style={{ marginTop: 12, alignSelf: 'flex-start' }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <ThemedText style={{ color: tintColor, fontSize: 16 }}>
                  {expandedMore.has(voter.voter_id) ? 'Less' : 'More'}
                </ThemedText>
                <MaterialIcons 
                  name={expandedMore.has(voter.voter_id) ? "expand-less" : "expand-more"} 
                  size={20} 
                  color={tintColor} 
                />
              </View>
            </TouchableOpacity>

            {/* Collapsible section */}
            {expandedMore.has(voter.voter_id) && (
              <>
                <TextInput
                  style={[
                    AppStyles.issuesInput,
                    {
                      backgroundColor: inputBackground,
                      borderColor: focusedVoterId === voter.voter_id ? '#007AFF' : inputBorder,
                      color: textColor,
                      height: inputHeights[voter.voter_id] || AppStyles.issuesInput.height,
                      marginTop: 12,
                    },
                    focusedVoterId === voter.voter_id && {
                      shadowColor: '#007AFF',
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 2,
                    }
                  ]}
                  value={localTextValues[voter.voter_id]?.key_issues ?? voter.key_issues ?? ''}
                  onChangeText={text => handleIssuesChange(voter.voter_id, text)}
                  placeholder="Enter issues"
                  placeholderTextColor={iconColor}
                  multiline
                  numberOfLines={2}
                  maxLength={300}
                  onFocus={() => setFocusedVoterId(voter.voter_id)}
                  onBlur={() => {
                    setFocusedVoterId(null);
                    handleIssuesBlur(voter.voter_id);
                  }}
                  onContentSizeChange={e => {
                    const minHeight = 48;
                    const maxHeight = 240; // 5 lines
                    let newHeight = e.nativeEvent.contentSize.height;
                    if (newHeight < minHeight) newHeight = minHeight;
                    if (newHeight > maxHeight) newHeight = maxHeight;
                    setInputHeights(h => ({ ...h, [voter.voter_id]: newHeight }));
                  }}
                />

            {/* Phone Number */}
            <View style={{ marginTop: 8 }}>
              {voter.have_phone_number && !editingPhone.has(voter.voter_id) ? (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: 4,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <ThemedText style={{ color: textColor }}>
                      Phone number on file
                    </ThemedText>
                  </View>
                  <TouchableOpacity
                    onPress={() => setEditingPhone(prev => new Set(prev).add(voter.voter_id))}
                    style={AppStyles.primaryButton}
                  >
                    <ThemedText style={AppStyles.primaryButtonText}>
                      Change
                    </ThemedText>
                  </TouchableOpacity>
                </View>
              ) : (
                <PhoneNumberInput
                  value={localTextValues[voter.voter_id]?.phone_number ?? voter.phone_number ?? ''}
                  onChangeText={text => handlePhoneChange(voter.voter_id, text)}
                  onBlur={() => handlePhoneBlur(voter.voter_id)}
                  placeholder="Phone number"
                  placeholderTextColor={iconColor}
                  style={{
                    backgroundColor: inputBackground,
                    borderColor: focusedVoterId === `${voter.voter_id}_phone` ? '#007AFF' : inputBorder,
                  }}
                />
              )}
            </View>

            {/* Email */}
            <View>
              {voter.have_email_address && !editingEmail.has(voter.voter_id) ? (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: 4,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <ThemedText style={{ color: textColor }}>
                      Email address on file
                    </ThemedText>
                  </View>
                  <TouchableOpacity
                    onPress={() => setEditingEmail(prev => new Set(prev).add(voter.voter_id))}
                    style={AppStyles.primaryButton}
                  >
                    <ThemedText style={AppStyles.primaryButtonText}>
                      Change
                    </ThemedText>
                  </TouchableOpacity>
                </View>
              ) : (
                <TextInput
                  style={[
                    AppStyles.issuesInput,
                    {
                      backgroundColor: inputBackground,
                      borderColor: focusedVoterId === `${voter.voter_id}_email` ? '#007AFF' : inputBorder,
                      color: textColor,
                    },
                    focusedVoterId === `${voter.voter_id}_email` && {
                      shadowColor: '#007AFF',
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 2,
                    }
                  ]}
                  value={localTextValues[voter.voter_id]?.email ?? voter.email ?? ''}
                  onChangeText={text => handleEmailChange(voter.voter_id, text)}
                  placeholder="Email address"
                  placeholderTextColor={iconColor}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  maxLength={100}
                  onFocus={() => setFocusedVoterId(`${voter.voter_id}_email`)}
                  onBlur={() => {
                    setFocusedVoterId(null);
                    handleEmailBlur(voter.voter_id);
                  }}
                />
              )}
            </View>

            {/* Reform Materials Checkboxes */}
            <View style={{ marginTop: 8, gap: 8 }}>
              <TouchableOpacity
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                }}
                onPress={() => {
                  const currentValue = voter.reform_window_poster;
                  const newValue = currentValue === null || currentValue === undefined || currentValue === ''? 'wants' : null;
                  handleGotvChange(voter.voter_id, 'reform_window_poster', newValue);
                }}
              >
                <MaterialIcons 
                  name={voter.reform_window_poster ? 'check-box' : 'check-box-outline-blank'} 
                  size={24} 
                  color={voter.reform_window_poster ? tintColor : iconColor} 
                />
                <ThemedText style={{ color: textColor, fontSize: 16 }}>
                  Wants window poster
                </ThemedText>
              </TouchableOpacity>

              <TouchableOpacity
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                }}
                onPress={() => {
                  const currentValue = voter.reform_garden_board;
                  const newValue = currentValue === null || currentValue === undefined || currentValue === ''? 'wants' : null;
                  handleGotvChange(voter.voter_id, 'reform_garden_board', newValue);
                }}
              >
                <MaterialIcons 
                  name={voter.reform_garden_board ? 'check-box' : 'check-box-outline-blank'} 
                  size={24} 
                  color={voter.reform_garden_board ? tintColor : iconColor} 
                />
                <ThemedText style={{ color: textColor, fontSize: 16 }}>
                  Wants garden board
                </ThemedText>
              </TouchableOpacity>
            </View>
              </>
            )}
          </View>
        ))}
        <View style={AppStyles.householdCloseButtonContainer}>
          <TouchableOpacity
            style={AppStyles.primaryButton}
            onPress={handleClose}
          >
            <ThemedText style={AppStyles.primaryButtonText}>Close</ThemedText>
          </TouchableOpacity>
        </View>
        <View style={{ height: 16 }} />
      </ScrollView>
      </KeyboardAvoidingView>

      {/* VI Dropdown Modal - Moved outside ScrollView */}
      {voterEdits.map((voter: Voter) => (
        <Modal
          key={`vi-modal-${voter.voter_id}`}
          visible={viDropdownVisible === voter.voter_id}
          transparent
          animationType="fade"
          onRequestClose={() => setViDropdownVisible(null)}
        >
          <TouchableOpacity style={AppStyles.modalContainer} activeOpacity={1} onPress={() => setViDropdownVisible(null)}>
            <View style={[AppStyles.modalView, { backgroundColor: modalBackground }]}>
              <TouchableOpacity
                style={AppStyles.modalCloseButton}
                onPress={() => setViDropdownVisible(null)}
                accessibilityLabel="Close dialog"
                accessibilityHint="Closes the voting intention selection dialog"
              >
                <MaterialIcons name="close" size={20} color={iconColor} />
              </TouchableOpacity>
              <ScrollView 
                style={{ maxHeight: '100%' }}
                showsVerticalScrollIndicator={true}
                bounces={false}
              >
                {VI_OPTIONS.map((item) => (
                  <TouchableOpacity
                    key={item.code || 'clear'}
                    style={AppStyles.viDropdownItem}
                    onPress={() => {
                      setViDropdownVisible(null);
                      handleViChange(voter.voter_id, item.code);
                    }}
                  >
                    <>
                      <View style={AppStyles.viDropdownItemRow}>
                        {/* Color indicator box */}
                        <View 
                          style={[
                            AppStyles.viColorIndicator,
                            { backgroundColor: item.color },
                            // Add border for light colors to ensure visibility
                            getBorderColorForLightColor(item.color) && {
                              borderWidth: 1,
                              borderColor: getBorderColorForLightColor(item.color)
                            }
                          ]}
                        />
                        <ThemedText style={[AppStyles.viDropdownItemCode, { color: textColor }]}>{item.code}</ThemedText>
                        <ThemedText style={[AppStyles.viDropdownItemLabel, { color: textColor }]}>{item.label}</ThemedText>
                      </View>
                      {(item.code === 'Z' || item.code === 'S' || item.code === 'W') && (
                        <View style={AppStyles.dropdownDivider} />
                      )}
                    </>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </TouchableOpacity>
        </Modal>
      ))}

      {/* GOTV Dropdown Modal - Moved outside ScrollView */}
      {voterEdits.map((voter: Voter) => (
        <Modal
          key={`gotv-modal-${voter.voter_id}`}
          visible={gotvDropdownVisible === voter.voter_id}
          transparent
          animationType="fade"
          onRequestClose={() => setGotvDropdownVisible(null)}
        >
          <TouchableOpacity style={AppStyles.modalContainer} activeOpacity={1} onPress={() => setGotvDropdownVisible(null)}>
            <View style={[AppStyles.modalView, { backgroundColor: modalBackground }]}>
              <TouchableOpacity
                style={AppStyles.modalCloseButton}
                onPress={() => setGotvDropdownVisible(null)}
                accessibilityLabel="Close dialog"
                accessibilityHint="Closes the GOTV status selection dialog"
              >
                <MaterialIcons name="close" size={20} color={iconColor} />
              </TouchableOpacity>
              <ScrollView 
                style={{ maxHeight: '100%' }}
                showsVerticalScrollIndicator={true}
                bounces={false}
              >
                {getGotvOptions(voter).map((item) => (
                  <TouchableOpacity
                    key={item.code || 'clear'}
                    style={AppStyles.viDropdownItem}
                    onPress={() => {
                      setGotvDropdownVisible(null);
                      handleGotvStatusChange(voter.voter_id, item.code);
                    }}
                  >
                    <>
                      <View style={AppStyles.viDropdownItemRow}>
                        {/* Icon indicator */}
                        <MaterialIcons 
                          name={item.icon as any} 
                          size={18} 
                          color={textColor} 
                          style={{ marginRight: 8 }} 
                        />
                        {item.letter && (
                          <ThemedText style={[AppStyles.viDropdownItemLabel, { color: textColor, fontSize: 12, fontWeight: 'bold', marginRight: 8 }]}>{item.letter}</ThemedText>
                        )}
                        <ThemedText style={[AppStyles.viDropdownItemLabel, { color: textColor }]}>{item.label}</ThemedText>
                      </View>
                    </>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </TouchableOpacity>
        </Modal>
      ))}

      {/* Not In Confirmation Modal - Moved outside ScrollView */}
      <Modal
        visible={showNotInDialog}
        transparent
        animationType="fade"
        onRequestClose={cancelNotIn}
      >
        <TouchableOpacity style={AppStyles.modalContainer} activeOpacity={1} onPress={cancelNotIn}>
          <View style={[AppStyles.modalView, { backgroundColor: modalBackground }]}>
            <ThemedText style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16, textAlign: 'center' }}>
              Not In
            </ThemedText>
            <ThemedText style={{ fontSize: 16, marginBottom: 24, textAlign: 'center', lineHeight: 22 }}>
              Are you sure you want to mark this household as &quot;Not In&quot;? This will record that no one was home.
            </ThemedText>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
              <TouchableOpacity
                style={[AppStyles.primaryButton, { flex: 1, backgroundColor: '#6c757d' }]}
                onPress={cancelNotIn}
              >
                <ThemedText style={AppStyles.primaryButtonText}>Cancel</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[AppStyles.primaryButton, { flex: 1, backgroundColor: '#dc3545' }]}
                onPress={confirmNotIn}
              >
                <ThemedText style={AppStyles.primaryButtonText}>Not In</ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Phone Number Modal for Lift Requests */}
      <Modal
        visible={showPhoneModal}
        transparent
        animationType="fade"
        onRequestClose={handlePhoneModalCancel}
      >
        <TouchableOpacity style={AppStyles.modalContainer} activeOpacity={1} onPress={handlePhoneModalCancel}>
          <TouchableOpacity 
            style={[AppStyles.modalView, { backgroundColor: modalBackground }]} 
            activeOpacity={1} 
            onPress={(e) => e.stopPropagation()}
          >
            <TouchableOpacity
              style={AppStyles.modalCloseButton}
              onPress={handlePhoneModalCancel}
              accessibilityLabel="Close dialog"
              accessibilityHint="Closes the phone number input dialog"
            >
              <MaterialIcons name="close" size={20} color={iconColor} />
            </TouchableOpacity>
            
            <ThemedText style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16, textAlign: 'center' }}>
              Phone Number Required
            </ThemedText>
            
            <ThemedText style={{ fontSize: 16, marginBottom: 24, textAlign: 'center', lineHeight: 22 }}>
              A phone number is required to request a lift. Please enter the voter&apos;s phone number below.
            </ThemedText>
            
            <View style={{ marginBottom: 24 }}>
              <PhoneNumberInput
                value={phoneModalNumber}
                onChangeText={setPhoneModalNumber}
                placeholder="Enter phone number"
                onValidationChange={(isValid) => setPhoneModalValid(isValid)}
                style={{ backgroundColor: inputBackground, borderColor: inputBorder }}
              />
            </View>
            
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
              <TouchableOpacity
                style={[AppStyles.primaryButton, { flex: 1, backgroundColor: '#6c757d' }]}
                onPress={handlePhoneModalCancel}
              >
                <ThemedText style={AppStyles.primaryButtonText}>Cancel</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  AppStyles.primaryButton, 
                  { 
                    flex: 1, 
                    backgroundColor: phoneModalValid ? tintColor : '#ccc',
                    opacity: phoneModalValid ? 1 : 0.6
                  }
                ]}
                onPress={handlePhoneModalConfirm}
                disabled={!phoneModalValid}
              >
                <ThemedText style={AppStyles.primaryButtonText}>Request Lift</ThemedText>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Reform Dialog - Shows after selecting "R" as VI */}
      {reformDialogVoterId && (() => {
        const voter = voterEdits.find(v => v.voter_id === reformDialogVoterId);
        return voter ? (
          <Modal
            visible={showReformDialog}
            transparent
            animationType="fade"
            onRequestClose={handleReformDialogCancel}
          >
            <TouchableOpacity style={AppStyles.modalContainer} activeOpacity={1} onPress={handleReformDialogCancel}>
              <TouchableOpacity 
                style={[AppStyles.modalView, { backgroundColor: modalBackground }]} 
                activeOpacity={1}
                onPress={(e) => e.stopPropagation()}
              >
                <TouchableOpacity
                  style={AppStyles.modalCloseButton}
                  onPress={handleReformDialogCancel}
                  accessibilityLabel="Close dialog"
                  accessibilityHint="Closes the reform supporter information dialog"
                >
                  <MaterialIcons name="close" size={20} color={iconColor} />
                </TouchableOpacity>
                
                <ScrollView 
                  showsVerticalScrollIndicator={true}
                  bounces={false}
                  contentContainerStyle={{ paddingBottom: 16 }}
                >
                  <ThemedText style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 24, textAlign: 'center' }}>
                    Reform Supporter Information
                  </ThemedText>
                  
                  {/* Campaign Updates Section */}
                  <ThemedText style={{ fontSize: 16, fontWeight: '600', marginBottom: 12, color: textColor }}>
                    Would you like to receive updates about the campaign?
                  </ThemedText>
                  
                  {/* Phone Number */}
                  <View style={{ marginBottom: 16 }}>
                    {voter.have_phone_number && !reformDialogEditingPhone ? (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingVertical: 4,
                        }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ThemedText style={{ color: textColor }}>
                            Phone number on file
                          </ThemedText>
                        </View>
                        <TouchableOpacity
                          onPress={() => setReformDialogEditingPhone(true)}
                          style={AppStyles.primaryButton}
                        >
                          <ThemedText style={AppStyles.primaryButtonText}>
                            Change
                          </ThemedText>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <PhoneNumberInput
                        value={reformDialogPhoneNumber}
                        onChangeText={setReformDialogPhoneNumber}
                        placeholder="Phone number"
                        placeholderTextColor={iconColor}
                        style={{
                          backgroundColor: inputBackground,
                          borderColor: inputBorder,
                        }}
                      />
                    )}
                  </View>

                  {/* Email */}
                  <View style={{ marginBottom: 24 }}>
                    {voter.have_email_address && !reformDialogEditingEmail ? (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingVertical: 4,
                        }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ThemedText style={{ color: textColor }}>
                            Email address on file
                          </ThemedText>
                        </View>
                        <TouchableOpacity
                          onPress={() => setReformDialogEditingEmail(true)}
                          style={AppStyles.primaryButton}
                        >
                          <ThemedText style={AppStyles.primaryButtonText}>
                            Change
                          </ThemedText>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <TextInput
                        style={[
                          AppStyles.issuesInput,
                          {
                            backgroundColor: inputBackground,
                            borderColor: inputBorder,
                            color: textColor,
                          },
                        ]}
                        value={reformDialogEmail}
                        onChangeText={setReformDialogEmail}
                        placeholder="Email address"
                        placeholderTextColor={iconColor}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        maxLength={100}
                      />
                    )}
                  </View>

                  {/* Sign Preferences Section */}
                  <ThemedText style={{ fontSize: 16, fontWeight: '600', marginBottom: 12, color: textColor }}>
                    Would you be willing to put up a sign?
                  </ThemedText>
                  
                  {/* Garden Board */}
                  <TouchableOpacity
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 12,
                    }}
                    onPress={() => {
                      const newValue = reformDialogGardenBoard === null || reformDialogGardenBoard === undefined || reformDialogGardenBoard === '' ? 'wants' : null;
                      setReformDialogGardenBoard(newValue);
                    }}
                  >
                    <MaterialIcons 
                      name={reformDialogGardenBoard ? 'check-box' : 'check-box-outline-blank'} 
                      size={24} 
                      color={reformDialogGardenBoard ? tintColor : iconColor} 
                    />
                    <ThemedText style={{ color: textColor, fontSize: 16 }}>
                      Garden Board
                    </ThemedText>
                  </TouchableOpacity>

                  {/* Window Poster */}
                  <TouchableOpacity
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 24,
                    }}
                    onPress={() => {
                      const newValue = reformDialogWindowPoster === null || reformDialogWindowPoster === undefined || reformDialogWindowPoster === '' ? 'wants' : null;
                      setReformDialogWindowPoster(newValue);
                    }}
                  >
                    <MaterialIcons 
                      name={reformDialogWindowPoster ? 'check-box' : 'check-box-outline-blank'} 
                      size={24} 
                      color={reformDialogWindowPoster ? tintColor : iconColor} 
                    />
                    <ThemedText style={{ color: textColor, fontSize: 16 }}>
                      Window Poster
                    </ThemedText>
                  </TouchableOpacity>
                </ScrollView>
                
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, marginTop: 16 }}>
                  <TouchableOpacity
                    style={[AppStyles.primaryButton, { flex: 1, backgroundColor: '#6c757d' }]}
                    onPress={handleReformDialogCancel}
                  >
                    <ThemedText style={AppStyles.primaryButtonText}>Cancel</ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[AppStyles.primaryButton, { flex: 1 }]}
                    onPress={handleReformDialogConfirm}
                  >
                    <ThemedText style={AppStyles.primaryButtonText}>Save</ThemedText>
                  </TouchableOpacity>
                </View>
              </TouchableOpacity>
            </TouchableOpacity>
          </Modal>
        ) : null;
      })()}
    </ThemedView>
  );
}
